import React from 'react';
import {
    Card,
    Row,
    Col,
    Statistic,
    Table,
    Tag,
    Progress,
    Alert,
    Button,
    Space,
    Spin,
    Empty,
    Typography
} from 'antd';
import {
    ThunderboltOutlined,
    ReloadOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    CloseCircleOutlined,
    SyncOutlined
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from '@/components/common/ThemeProvider';

const { Title } = Typography;

interface CircuitBreakerStatus {
    name: string;
    state: string;
    failureRate: number;
    slowCallRate: number;
    numberOfBufferedCalls: number;
    numberOfFailedCalls: number;
    numberOfSuccessfulCalls: number;
    numberOfSlowCalls: number;
    numberOfSlowSuccessfulCalls: number;
    numberOfSlowFailedCalls: number;
    numberOfNotPermittedCalls: number;
}

interface BulkheadStatus {
    name: string;
    availableConcurrentCalls: number;
    maxAllowedConcurrentCalls: number;
    utilizationPercent: number;
}

interface CircuitBreakerMetrics {
    circuitBreakers: CircuitBreakerStatus[];
    bulkheads: BulkheadStatus[];
    totalCircuitBreakers: number;
    openCircuitBreakers: number;
    halfOpenCircuitBreakers: number;
    closedCircuitBreakers: number;
    totalBulkheads: number;
}

const useCircuitBreakerMetrics = () => {
    return useQuery({
        queryKey: ['/api/service-metrics/circuit-breaker/summary'],
        queryFn: async () => {
            const response = await fetch('/api/service-metrics/circuit-breaker/summary');
            if (!response.ok) throw new Error('Failed to fetch circuit breaker metrics');
            return response.json() as Promise<CircuitBreakerMetrics>;
        },
        refetchInterval: 5000, // Refresh every 5 seconds
    });
};

const CircuitBreakerDashboard: React.FC = () => {
    const { dark } = useTheme();
    const { data: metrics, isLoading, refetch, error } = useCircuitBreakerMetrics();

    const getStateColor = (state: string) => {
        switch (state) {
            case 'CLOSED': return 'success';
            case 'HALF_OPEN': return 'warning';
            case 'OPEN': return 'error';
            default: return 'default';
        }
    };

    const getStateIcon = (state: string) => {
        switch (state) {
            case 'CLOSED': return <CheckCircleOutlined />;
            case 'HALF_OPEN': return <SyncOutlined spin />;
            case 'OPEN': return <CloseCircleOutlined />;
            default: return <ExclamationCircleOutlined />;
        }
    };

    const circuitBreakerColumns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text: string) => <strong>{text}</strong>,
        },
        {
            title: 'State',
            dataIndex: 'state',
            key: 'state',
            render: (state: string) => (
                <Tag color={getStateColor(state)} icon={getStateIcon(state)}>
                    {state}
                </Tag>
            ),
        },
        {
            title: 'Failure Rate',
            dataIndex: 'failureRate',
            key: 'failureRate',
            render: (rate: number) => (
                <Progress 
                    percent={rate} 
                    size="small" 
                    status={rate > 50 ? 'exception' : rate > 25 ? 'active' : 'success'}
                    format={(p) => `${p?.toFixed(1)}%`}
                />
            ),
        },
        {
            title: 'Slow Call Rate',
            dataIndex: 'slowCallRate',
            key: 'slowCallRate',
            render: (rate: number) => (
                <Progress 
                    percent={rate} 
                    size="small" 
                    status={rate > 50 ? 'exception' : rate > 25 ? 'active' : 'success'}
                    format={(p) => `${p?.toFixed(1)}%`}
                />
            ),
        },
        {
            title: 'Buffered',
            dataIndex: 'numberOfBufferedCalls',
            key: 'numberOfBufferedCalls',
        },
        {
            title: 'Success',
            dataIndex: 'numberOfSuccessfulCalls',
            key: 'numberOfSuccessfulCalls',
            render: (val: number) => <Tag color="success">{val}</Tag>,
        },
        {
            title: 'Failed',
            dataIndex: 'numberOfFailedCalls',
            key: 'numberOfFailedCalls',
            render: (val: number) => <Tag color={val > 0 ? 'error' : 'default'}>{val}</Tag>,
        },
        {
            title: 'Slow',
            dataIndex: 'numberOfSlowCalls',
            key: 'numberOfSlowCalls',
            render: (val: number) => <Tag color={val > 0 ? 'warning' : 'default'}>{val}</Tag>,
        },
        {
            title: 'Not Permitted',
            dataIndex: 'numberOfNotPermittedCalls',
            key: 'numberOfNotPermittedCalls',
            render: (val: number) => <Tag color={val > 0 ? 'error' : 'default'}>{val}</Tag>,
        },
    ];

    const bulkheadColumns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text: string) => <strong>{text}</strong>,
        },
        {
            title: 'Available Calls',
            dataIndex: 'availableConcurrentCalls',
            key: 'availableConcurrentCalls',
        },
        {
            title: 'Max Allowed',
            dataIndex: 'maxAllowedConcurrentCalls',
            key: 'maxAllowedConcurrentCalls',
        },
        {
            title: 'Utilization',
            dataIndex: 'utilizationPercent',
            key: 'utilizationPercent',
            render: (percent: number, record: BulkheadStatus) => (
                <Progress 
                    percent={percent} 
                    size="small" 
                    status={percent > 80 ? 'exception' : percent > 50 ? 'active' : 'success'}
                    format={() => `${record.maxAllowedConcurrentCalls - record.availableConcurrentCalls}/${record.maxAllowedConcurrentCalls}`}
                />
            ),
        },
    ];

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-60">
                <Spin size="large" tip="Loading circuit breaker metrics..." />
            </div>
        );
    }

    if (error) {
        return (
            <Alert
                message="Error Loading Metrics"
                description="Failed to load circuit breaker metrics. Please try again."
                type="error"
                showIcon
            />
        );
    }

    const hasOpenCircuitBreakers = (metrics?.openCircuitBreakers || 0) > 0;
    const hasHalfOpenCircuitBreakers = (metrics?.halfOpenCircuitBreakers || 0) > 0;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <Space>
                    <ThunderboltOutlined className="text-2xl text-yellow-500" />
                    <Title level={4} style={{ margin: 0 }}>
                        Circuit Breaker & Bulkhead Status
                    </Title>
                </Space>
                <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
                    Refresh
                </Button>
            </div>

            {/* Alerts */}
            {hasOpenCircuitBreakers && (
                <Alert
                    message={`${metrics?.openCircuitBreakers} Circuit Breaker(s) OPEN`}
                    description="Some services are currently unavailable due to high failure rates. The system is protecting itself from cascading failures."
                    type="error"
                    showIcon
                    icon={<CloseCircleOutlined />}
                />
            )}

            {hasHalfOpenCircuitBreakers && (
                <Alert
                    message={`${metrics?.halfOpenCircuitBreakers} Circuit Breaker(s) HALF-OPEN`}
                    description="Some circuit breakers are testing if the underlying service has recovered."
                    type="warning"
                    showIcon
                    icon={<SyncOutlined spin />}
                />
            )}

            {/* Summary Statistics */}
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Total Circuit Breakers"
                            value={metrics?.totalCircuitBreakers || 0}
                            prefix={<ThunderboltOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Closed (Healthy)"
                            value={metrics?.closedCircuitBreakers || 0}
                            valueStyle={{ color: '#3f8600' }}
                            prefix={<CheckCircleOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Half-Open (Testing)"
                            value={metrics?.halfOpenCircuitBreakers || 0}
                            valueStyle={{ color: '#faad14' }}
                            prefix={<SyncOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Open (Tripped)"
                            value={metrics?.openCircuitBreakers || 0}
                            valueStyle={{ color: (metrics?.openCircuitBreakers || 0) > 0 ? '#cf1322' : '#3f8600' }}
                            prefix={<CloseCircleOutlined />}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Circuit Breakers Table */}
            <Card title="Circuit Breakers">
                {metrics?.circuitBreakers && metrics.circuitBreakers.length > 0 ? (
                    <Table
                        columns={circuitBreakerColumns}
                        dataSource={metrics.circuitBreakers}
                        rowKey="name"
                        pagination={false}
                        size="small"
                        scroll={{ x: 1000 }}
                    />
                ) : (
                    <Empty description="No circuit breakers registered" />
                )}
            </Card>

            {/* Bulkheads Table */}
            <Card title={`Bulkheads (Concurrent Call Limiters) - ${metrics?.totalBulkheads || 0} registered`}>
                {metrics?.bulkheads && metrics.bulkheads.length > 0 ? (
                    <Table
                        columns={bulkheadColumns}
                        dataSource={metrics.bulkheads}
                        rowKey="name"
                        pagination={false}
                        size="small"
                    />
                ) : (
                    <Empty description="No bulkheads registered" />
                )}
            </Card>
        </div>
    );
};

export default CircuitBreakerDashboard;
